import {
  addDisplayName,
  addHandlers,
  addProps,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { FC, KeyboardEvent } from 'react'
import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { branchIfTruthy } from 'ad-hok-utils'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { BODY_MEDIUM_STYLE } from '../styles'
import Icon from '../Icon'

const HEIGHT = 64
const BORDER_RADIUS = 2

const useStyles = createUseStyles({
  skeleton: {
    backgroundColor: 'rgba(35, 35, 35, 0.02)',
  },
  disabled: {
    flexGrow: 1,
    height: HEIGHT,
  },
  container: {
    position: 'relative',
    display: 'flex',
    flexGrow: 1,
  },
  fieldContainer: {
    flexGrow: 1,
    height: HEIGHT,
  },
  input: {
    ...BODY_MEDIUM_STYLE,
    color: 'rgba(54, 52, 50, 0.96)',
    width: '100%',
    height: '100%',
    background: 'rgba(54, 52, 50, 0.02)',
    borderRadius: BORDER_RADIUS,
    border: '1px solid rgba(144, 115, 79, 0.24)',
    paddingRight: HEIGHT,

    '&:focus': {
      outline: 'none',
      border: '1px solid rgba(144, 115, 79, 0.56)',
    },

    '&::placeholder': {
      color: 'rgba(54, 52, 50, 0.96)',
      opacity: 0.8,
    },
  },
  inputWithoutPasteIcon: {
    paddingLeft: 16,
  },
  inputWithPasteIcon: {
    paddingLeft: 56,
  },
  pasteIconContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    paddingLeft: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#CBA67A',
    pointerEvents: 'none',
  },
  button: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderTopRightRadius: BORDER_RADIUS,
    borderBottomRightRadius: BORDER_RADIUS,
  },
  buttonEnabled: {
    background: 'rgba(54, 52, 50, 0.96)',
    color: 'white',
  },
  buttonDisabled: {
    background: 'transparent',
    color: 'rgba(144, 115, 79, 0.24)',
  },
})

interface ProductFieldSkeletonProps {
  className?: string
}

export const ProductFieldSkeleton: FC<ProductFieldSkeletonProps> = flowMax(
  addDisplayName('ProductFieldSkeleton'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ classes, className }) => (
    <div className={cx(classes.skeleton, className)} />
  )
)

interface ButtonProps {
  onClick: () => void
  disabled?: boolean
}

const Button: FC<ButtonProps> = flowMax(
  addDisplayName('Button'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ onClick, disabled, classes }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cx(
        classes.button,
        disabled ? classes.buttonDisabled : classes.buttonEnabled
      )}
    >
      <Icon name="next" />
    </button>
  )
)

const urlWithScheme = (url: string): string =>
  url.indexOf('http') === 0 ? url : `https://${url}`

interface Props {
  onNewWithUrl: (url: string) => void
  disabled?: boolean
}

export const ProductField: FC<Props> = flowMax(
  addDisplayName('ProductField'),
  addStateHandlers(
    {
      url: '',
    },
    {
      onChangeUrl: () => (event: React.ChangeEvent<HTMLInputElement>) => ({
        url: event.target.value,
      }),
    }
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  branchIfTruthy('disabled', {
    returns: ({ classes }) => (
      <ProductFieldSkeleton className={classes.disabled} />
    ),
  }),
  addHandlers({
    onNextClick:
      ({ onNewWithUrl, url }) =>
      () => {
        onNewWithUrl(urlWithScheme(url))
      },
  }),
  addHandlers({
    onKeyPress:
      ({ onNextClick }) =>
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter') {
          return
        }

        onNextClick()
      },
  }),
  addTranslationHelpers,
  ({ url, onChangeUrl, onNextClick, onKeyPress, classes, t }) => (
    <div className={classes.container}>
      <div className={classes.fieldContainer}>
        <input
          type="url"
          placeholder={t('admin.post.productPlaceholder')}
          value={url}
          onChange={onChangeUrl}
          onKeyPress={onKeyPress}
          className={cx(
            classes.input,
            url ? classes.inputWithoutPasteIcon : classes.inputWithPasteIcon
          )}
        />
      </div>
      {!url && (
        <div className={classes.pasteIconContainer}>
          <Icon name="copy" />
        </div>
      )}
      <Button onClick={onNextClick} disabled={!url} />
    </div>
  )
)
